import React from 'react';

import { createColumnHelper } from '@tanstack/react-table';

import {
  fetchResponseWithToken,
  getApigeeUrl,
} from '@adsk/acs-sc-web-platform-api';
import { PRODUCT_IDS } from '@adsk/alloy-provider';
import Checkbox from '@adsk/alloy-react-checkbox';

import { THUMBNAIL_SIZE_TYPE, COLLECTION_DIVIDER_KEY } from './consts';
import Services from './sdkServices';
import CollectionCell from '../components/CollectionCell';
import SheetInfoCell from '../components/SheetInfoCell';
import TagCell from '../components/TagCell';
import VersionSetCell from '../components/VersionSetCell';

const columnHelper = createColumnHelper();

export const getThumbnail = async (
  bubbleUrn,
  bubbleViewableGuid,
  scopes,
  type = THUMBNAIL_SIZE_TYPE.SMALL,
) => {
  try {
    const path = await Services.DerivativeService.getThumbnailByUrn(
      bubbleUrn,
      {
        scopes,
        type,
        guid: bubbleViewableGuid,
      },
      { skipBlob: true },
    );
    const res = await fetchResponseWithToken(`${getApigeeUrl()}${path}`);
    if (res.status !== 200) return undefined;
    const blob = await res.blob();
    return URL.createObjectURL(blob);
  } catch (error) {
    return undefined;
  }
};

export const chunk = (arr, size) => {
  const temp = [...arr];
  const results = [];
  while (temp.length) {
    results.push(temp.splice(0, size));
  }
  return results;
};

export const formatSheetHistory = (sheet) => ({
  ...sheet,
  sheetId: sheet.id,
  id: sheet.historyId,
});

export const formatSheetVersion = (sheet) => ({
  ...sheet,
  sheetId: sheet.id,
});

export const isDefaultCollection = (collection) =>
  collection?.type === 'default';

export const isUserCollection = (collection) => collection?.type === 'normal';

export const partition = (collection, predicate) => {
  const firstList = collection.filter(predicate);
  const secondList = collection.filter((item) => !predicate(item));

  return [firstList, secondList];
};

export const getDisplayCollections = (collections, selectCountMap) => {
  if (collections.length === 0) return [];

  collections = collections.map((collection) => ({
    ...collection,
    selectedSheetsCount: selectCountMap[collection.id] || 0,
  }));

  const [defaults, users] = partition(collections, isDefaultCollection);

  // Don't show ungrouped when no user-created
  if (users.length === 0) return [];
  // Don't show ungrouped when sheets is empty
  if (!defaults[0] || defaults[0].sheetsCount === 0) return users;

  const hasDivider = collections.find(
    ({ id }) => id === COLLECTION_DIVIDER_KEY,
  );

  return hasDivider
    ? [...defaults, ...users]
    : [...defaults, { id: COLLECTION_DIVIDER_KEY }, ...users];
};

export const selectTableRows = ({
  val,
  multiple,
  isShiftSelect,
  table,
  row,
  lastSelectRowIdxRef,
}) => {
  const toSelect = !!val;
  if (multiple) {
    const { rowSelection } = table.getState();
    const { rows } = table.getRowModel();
    const selectedIds = Object.keys(rowSelection);

    const lastSelectedIdx =
      (lastSelectRowIdxRef.current || 0) < rows.length
        ? lastSelectRowIdxRef.current
        : null;
    lastSelectRowIdxRef.current = row.index;

    if (isShiftSelect) {
      // Clear document selection when shift key is pressed.
      document?.getSelection()?.removeAllRanges();

      if (lastSelectedIdx !== null && lastSelectedIdx !== row.index) {
        const [start, end] = [lastSelectedIdx, row.index].sort((a, b) => a - b);

        // Get all selectable rows in range
        const targetIds = rows
          .slice(start, end + 1)
          .filter((row) => row.getCanSelect())
          .map((row) => row.id);

        // Mutate the selectedIds array based on the shift selection.
        const updatedIds = [...selectedIds];
        targetIds.forEach((id) => {
          if (toSelect) {
            if (!updatedIds.includes(id)) updatedIds.push(id);
          } else {
            const idx = updatedIds.indexOf(id);
            if (idx !== -1) updatedIds.splice(idx, 1);
          }
        });

        // Update finial selection array
        table.setRowSelection(
          updatedIds.reduce((pre, id) => {
            pre[id] = true;
            return pre;
          }, {}),
        );

        return;
      }
    }
  }

  // For all other cases, just toggle the selection.
  row.toggleSelected?.(toSelect);
};

/** Get customized style for table cell */
export const getTableCellStyle = (cell) => {
  const columnKey = cell?.column?.id;
  // Ensure the selection column has fixed width
  return columnKey === 'selection' ? { style: { flex: 'none' } } : {};
};

/** Get customized style for header cell */
export const getTableHeaderStyle = (header) => {
  const columnKey = header?.column?.id;
  // Ensure the selection column has fixed width
  return columnKey === 'selection' ? { style: { flex: 'none' } } : {};
};

/** Get default selection column for table component. */
export const getSelectionColumn = () => {
  return columnHelper.display({
    id: 'selection',
    size: 40,
    enableResizing: false,
    header: ({ table }) => (
      <Checkbox
        checked={
          table.getIsSomeRowsSelected()
            ? 'indeterminate'
            : table.getIsAllRowsSelected()
        }
        onChange={(value) => {
          table.toggleAllRowsSelected(value);
        }}
      />
    ),
    cell: ({ row }) => (
      <Checkbox
        checked={
          row.getIsSomeSelected() ? 'indeterminate' : row.getIsSelected()
        }
        disabled={!row.getCanSelect()}
      />
    ),
  });
};

export const getSheetsColumns = ({
  T,
  disabledIds,
  thumbnails,
  searchText,
  filterTags,
  searchAll,
  setThumbnails,
  onTagClick,
  onCollectionClick,
}) => {
  return [
    columnHelper.accessor('number', {
      id: 'number',
      minSize: 200,
      enableResizing: false,
      header: T.translate('NUMBER'),
      cell: (props) => {
        const rowData = props.row.original;
        return (
          <SheetInfoCell
            entity={rowData}
            searchText={searchText}
            isDisabled={disabledIds.includes(rowData.id)}
            thumbnail={thumbnails[rowData.sheetId]}
            onThumbnailLoad={({ sheetId, thumbnail }) =>
              setThumbnails((prev) => ({ ...prev, [sheetId]: thumbnail }))
            }
          />
        );
      },
    }),
    searchAll &&
      columnHelper.accessor('collection', {
        id: 'collection',
        minSize: 150,
        enableResizing: false,
        header: T.translate('COLLECTION'),
        cell: (props) => {
          const rowData = props.row.original;
          return (
            <CollectionCell
              rowData={rowData}
              onCollectionClick={onCollectionClick}
              isDisabled={disabledIds.includes(rowData.id)}
            />
          );
        },
      }),
    columnHelper.accessor('versionSet', {
      id: 'versionSet',
      minSize: 200,
      enableResizing: false,
      header: T.translate('VERSION_SET'),
      cell: (props) => {
        const rowData = props.row.original;
        return (
          <VersionSetCell
            rowData={rowData}
            isDisabled={disabledIds.includes(rowData.id)}
          />
        );
      },
    }),
    columnHelper.accessor('tags', {
      id: 'tags',
      minSize: 200,
      header: T.translate('TAG'),
      enableResizing: false,
      cell: (props) => {
        const rowData = props.row.original;
        return (
          <TagCell
            rowData={rowData}
            filterTags={filterTags}
            onTagClick={onTagClick}
            isDisabled={disabledIds.includes(rowData.id)}
          />
        );
      },
    }),
  ].filter(Boolean);
};

export const filterSuggestions = (list, keyword, limit = 5) => {
  return (list || [])
    .filter(
      (val) =>
        !keyword || val.toLowerCase().indexOf(keyword.toLowerCase()) !== -1,
    )
    .slice(0, limit);
};

export const updateSuggestions = (list, keyword) => {
  if (!keyword) return list;

  if (!list || list.length === 0) {
    return [keyword];
  }

  const idx = list.findIndex((val) => val === keyword);
  if (idx !== -1) list.splice(idx, 1);
  list.unshift(keyword);
  return list.length > 100 ? list.slice(0, 100) : [...list];
};

const groupBy = (arr, groupKeyGetter) => {
  if (!Array.isArray(arr) || arr.length === 0) return {};
  return arr.reduce((pre, cur) => {
    const groupKey = groupKeyGetter(cur);
    if (groupKey) {
      if (pre[groupKey]) {
        pre[groupKey].push(cur);
      } else {
        pre[groupKey] = [cur];
      }
    }
    return pre;
  }, {});
};

export const getGroupedVersionSets = (
  versionSets,
  defaultGroupName,
  collectionList,
) => {
  const grouped = groupBy(versionSets, ({ collection }) => collection?.id);
  const result = [];
  collectionList.forEach(({ id, name, type }) => {
    const groupList = grouped[id];
    if (groupList && groupList.length > 0) {
      result.push({
        label: type === 'default' ? defaultGroupName : name,
        key: id,
        options: groupList,
      });
    }
  });
  return result;
};

export const isBuildSheets = (sheetsContainerType) =>
  sheetsContainerType === 'Build';

export const getProductId = (sheetsContainerType) =>
  isBuildSheets(sheetsContainerType) ? PRODUCT_IDS.BUILD : PRODUCT_IDS.TAKEOFF;
